import {LockIcon} from '@primer/octicons-react'
import {ActionList, Banner} from '@primer/react'
import {Suspense, useCallback} from 'react'
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {LazyRelationshipsBlockedByListViewRelayFragment$data} from './__generated__/LazyRelationshipsBlockedByListViewRelayFragment.graphql'
import type {LazyRelationshipsBlockedByListViewRelayQuery} from './__generated__/LazyRelationshipsBlockedByListViewRelayQuery.graphql'
import {
  PAGE_SIZE,
  type PaginatedRelationshipsListAllViewItemFragment,
  RelationshipsListAllViewPaginated,
  RelationshipsListItem,
  RelationshipsListViewLoadingSkeleton,
} from './PaginatedRelationshipsListAllView'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LazyRelationshipsBlockedByListViewRelayFragment = graphql`
  fragment LazyRelationshipsBlockedByListViewRelayFragment on Issue
  @refetchable(queryName: "LazyRelationshipsBlockedByListViewRelayPaginatedQuery")
  @argumentDefinitions(cursor: {type: "String", defaultValue: null}, pageSize: {type: "Int"}) {
    repository {
      nameWithOwner
    }
    blockedBy(first: $pageSize, after: $cursor)
      @connection(key: "LazyRelationshipsBlockedByListViewFragment__blockedBy") {
      edges {
        node {
          id
          url
          number
          titleHTML
          state
          stateReason
          repository {
            name
            nameWithOwner
            owner {
              login
            }
          }
          assignees(first: 10) {
            totalCount
            edges {
              node {
                id
                login
                avatarUrl
              }
            }
          }
          closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
            totalCount
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LazyRelationshipsBlockedByListViewRelayQueryGraphQL = graphql`
  query LazyRelationshipsBlockedByListViewRelayQuery($id: ID!, $cursor: String, $pageSize: Int) {
    issue: node(id: $id) {
      ...LazyRelationshipsBlockedByListViewRelayFragment @arguments(cursor: $cursor, pageSize: $pageSize)
    }
  }
`

type LazyRelationshipsBlockedByListViewRelayProps = {
  itemId: string
  blockedByCount: number
}

export function LazyRelationshipsBlockedByListViewRelay({
  itemId,
  blockedByCount,
}: LazyRelationshipsBlockedByListViewRelayProps) {
  return (
    <Suspense fallback={<RelationshipsListViewLoadingSkeleton />}>
      <RelationshipsBlockedByListViewRelayInternal
        itemId={itemId}
        pageSize={PAGE_SIZE}
        blockedByCount={blockedByCount}
      />
    </Suspense>
  )
}

type RelationshipsBlockedByListViewRelayProps = {
  itemId: string
  pageSize: number
  blockedByCount: number
}

function RelationshipsBlockedByListViewRelayInternal({
  itemId,
  pageSize,
  blockedByCount,
}: RelationshipsBlockedByListViewRelayProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsBlockedByListViewRelayQuery>(
    LazyRelationshipsBlockedByListViewRelayQueryGraphQL,
    {id: itemId, pageSize},
  )

  const issueFragment = queryData.issue
  const {data, loadNext, isLoadingNext} = usePaginationFragment(
    LazyRelationshipsBlockedByListViewRelayFragment,
    issueFragment,
  )
  const issueData = data as LazyRelationshipsBlockedByListViewRelayFragment$data | null
  const onLoadMore = useCallback(() => loadNext(PAGE_SIZE), [loadNext])

  if (!issueFragment || !issueData?.repository || !issueData.blockedBy?.edges) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyBlockedByList}</Banner.Title>
      </Banner>
    )
  }

  const nodes = issueData.blockedBy.edges.map(edge => edge?.node).filter(node => !!node)
  if (nodes.length === 0) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyBlockedByList}</Banner.Title>
      </Banner>
    )
  }

  const hasNextPage = issueData.blockedBy.pageInfo.hasNextPage
  const hasInaccessibleBlockedByIssues = nodes.length < blockedByCount && !hasNextPage

  return (
    <RelationshipsListAllViewPaginated
      accessibleTitle={LABELS.relationNames.blockedByIssues}
      isLoadingNext={isLoadingNext}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
    >
      {nodes.map(node => (
        <RelationshipsListItem
          key={node.id}
          issue={node as PaginatedRelationshipsListAllViewItemFragment}
          sourceIssueRepositoryNameWithOwner={issueData.repository.nameWithOwner}
        />
      ))}
      {hasInaccessibleBlockedByIssues && (
        <ActionList.Item disabled aria-disabled="true" className="tmp-pl-3">
          <ActionList.LeadingVisual>
            <LockIcon size={16} />
          </ActionList.LeadingVisual>
          <span className="text-small color-fg-muted" data-testid="private-blocked-by-notice-modal">
            Private issues are hidden.
          </span>
        </ActionList.Item>
      )}
    </RelationshipsListAllViewPaginated>
  )
}
